import React, {useCallback, useContext, useEffect, useRef, useState, useMemo} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import Button from "../ui-components/Button";
import {PlusCircleIcon, TrashIcon, PaperClipIcon} from "@heroicons/react/24/solid";
import {EditorState, convertToRaw, convertFromRaw} from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import createMentionPlugin, { defaultSuggestionsFilter } from '@draft-js-plugins/mention';
import 'draft-js/dist/Draft.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import RichUtils from "draft-js/lib/RichTextEditorUtil";
import AuthenticationService from "../services/AuthenticationService";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import useAsyncError from "../hooks/useAsyncError";
import {NotificationsContext} from "../ui-components/Notifications";
import Select from "../ui-components/Select";
import moment from "moment";
import {ArrowUturnRightIcon, PencilSquareIcon} from "@heroicons/react/24/outline";
import Modal from "../ui-components/Modal";
import Spinner from "../ui-components/Spinner";
import Modifier from "draft-js/lib/DraftModifier";
import UserNotificationsContext from "../UserNotificationsContext";
import NotificationPing from "../ui-components/NotificationPing";
import { useWorksiteUsers } from "../hooks/useWorksiteUsers";

import {SORT_OPTIONS_NOTES} from "../utils/constants";
import {ArrowPathIcon} from "@heroicons/react/20/solid";
import NotesAttachmentsSection from "./NotesAttachmentsSection";
// setup bad words filter
const ItalianBadWords = require('italian-badwords-list');
const BadWordsFilter = require('bad-words');
const badWordsFilter = new BadWordsFilter();
const badWordsVariants = [
  (word) => word,
  (word) => `dio${word}`,
  (word) => `${word}dio`,
  (word) => `${word}ddio`,
]
badWordsVariants.forEach((v) => {
  badWordsFilter.addWords(...ItalianBadWords.array.map(v));
});


// Constants
const NOTE_CLS = 'bg-gray-50 border border-gray-100 rounded-md shadow px-3 py-2 overflow-hidden';
const DEFAULT_NOTE = {
  content: EditorState.createEmpty(),
  mentions: []
}

const EDITOR_CONTROLS = {
  bold: {
    component: <span className="font-serif font-bold">B</span>,
    onClick: (state) => RichUtils.toggleInlineStyle(state, 'BOLD'),
  },
  italic: {
    component: <span className="font-serif italic">I</span>,
    onClick: (state) => RichUtils.toggleInlineStyle(state, 'ITALIC'),
  },
  underline: {
    component: <span className="font-serif underline">U</span>,
    onClick: (state) => RichUtils.toggleInlineStyle(state, 'UNDERLINE'),
  },
  mention: {
    component: <span>@</span>,
    onClick: (state) => {
      const currentContent = state.getCurrentContent(),
            currentSelection = state.getSelection();
      const newContent = Modifier.replaceText(
        currentContent,
        currentSelection,
        '@'
      );
      return EditorState.push(state, newContent, 'insert-characters');
    }
  },
}
const EDITOR_CONTROLS_INIT = Object.keys(EDITOR_CONTROLS).reduce(
  (prev, key) => ({...prev, [key]: false}),
  {}
)
const CAN_DELETE_OTHERS_COMMENTS = ['admin'];


const convertFromRawWithCensorship = (rawState) => {
  rawState.blocks.forEach((b, idx, arr) => {
    arr[idx] = {
      ...b,
      /*
      added to fix HUBSPOT ticket 2945948514
      there were blocks whose text was one or more spaces
      that made the badwords filter to crash
       */
      text: b.text.length > 0 && b.text.trim().length > 0
        ? badWordsFilter.clean(b.text)
        : b.text
    }
  })
  return convertFromRaw(rawState);
}


const WorksiteNotesSection = ({
    worksite,
    activityId = null,
    highlightedNoteId = null,
    infiniteScroll = true,
    paginationBuffer = 10,
    viewOnly = false,
    setCommentsCount = (prev) => {},
    defaultSortMode = 'desc',
}) => {

  const intl = useIntl();
  const userData = AuthenticationService.getUserData();
  const countryApiRequest = useCountryAxiosInstance();

  // context
  const {push} = useContext(NotificationsContext);
  const {userNotifications, setUserNotifications} = useContext(UserNotificationsContext);

  // refs
  const mainContainer = useRef(null);
  const editor = useRef(null);

  // error handling
  const throwError = useAsyncError();

  // state
  const [notes, setNotes] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);
  const [currentNoteAttachments, setCurrentNoteAttachments] = useState([]);
  const [sortMode, setSortMode] = useState(defaultSortMode);
  const [editorModifiers, setEditorModifiers] = useState(EDITOR_CONTROLS_INIT);
  const [submittingCurrentNote, setSubmittingCurrentNote] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [paginationSkip, setPaginationSkip] = useState(0);
  const [allNotesLoaded, setAllNotesLoaded] = useState(false);
  const [userSuggestionsOpen, setUserSuggestionsOpen] = useState(false);
  const [worksiteUsers] = useWorksiteUsers(worksite);
  const [mentionsSuggestions, setMentionsSuggestions] = useState(null);
  const [highlightedNoteFound, setHighlightedNoteFound] = useState(false);
  const [highlightedNoteClicked, setHighlightedNoteClicked] = useState(false);
  const [scrollToHighlightedNoteHappened, setScrollToHighlightedNoteHappened] = useState(highlightedNoteId === null);

  // --- setup keypress listener in order to detect key combinations in editor
  const handleKeyPress = ({ ctrlKey, metaKey, key }) => {
    // exit if not editing a note or CTRL/CMD key is not pressed
    if (!currentNote || !(ctrlKey || metaKey))
      return;
    // determine operation to be executed based on the key pressed with CTRL/CMD
    let control;
    if (key === 'b')
      control = 'bold';
    else if (key === 'i')
      control = 'italic';
    else if (key === 'u')
      control = 'underline';
    else
      return;
    // update note content
    setEditorModifiers((prev) => ({...prev, [control]: !prev[control]}))
    setCurrentNote((prev) => ({
      ...prev,
      content: EDITOR_CONTROLS[control].onClick(prev.content)
    }));
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  const isCurrentNoteEmpty = !currentNote?.content.getCurrentContent().hasText();
  const notesSorter = sortMode === 'asc'
    ? (a, b) => a.created_at > b.created_at ? 1 : -1
    : (a, b) => a.created_at > b.created_at ? -1 : 1;

  const { MentionSuggestions, plugins } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      mentionComponent: ({children}) => (
        <span className="cursor-default bg-blue-100 text-gray-600 px-0.5">
          {children}
        </span>
      ),
    });
    // eslint-disable-next-line no-shadow
    const { MentionSuggestions } = mentionPlugin;
    // eslint-disable-next-line no-shadow
    const plugins = [mentionPlugin];
    return { plugins, MentionSuggestions };
  }, []);

  const getEditorWithMention = (userId, userFullname) => {
    const state = EditorState.createEmpty();
    const stateWithEntity = state.getCurrentContent().createEntity(
      'mention',
      'IMMUTABLE',
      {
        mention: {
          id: userId,
          name: userFullname
        },
      },
    )
    const entityKey = stateWithEntity.getLastCreatedEntityKey()
    const stateWithText = Modifier.insertText(stateWithEntity, state.getSelection(), userFullname, null, entityKey);
    return EditorState.moveFocusToEnd(EditorState.push(state, stateWithText));
  }

  const sendEmailNotificationToUsers = async (noteId, recipients) => {
    await countryApiRequest.post(`worksites_notes/send_mentions_notifications`, {
      note_id: noteId,
      worksite_id: worksite.id,
      activity_id: activityId,
      worksite_name: worksite.des_worksite,
      recipients: recipients
    });
  }

  // ---------- INSERT/UPDATE NOTE METHOD
  const saveCurrentNote = async () => {
    if (!currentNote || isCurrentNoteEmpty)
      return;

    // extract mentions from content
    let rawEditorContent, currentMentions, newMentions, currentMentionsIds
    try{
      rawEditorContent = convertToRaw(currentNote.content.getCurrentContent());
      currentMentions = Object.values(rawEditorContent.entityMap).reduce(
        (prev, {data: {mention}}) => ({...prev, [mention.id]: mention.name}),
        {}
      );
      currentMentionsIds = Object.keys(currentMentions).map((sid) => parseInt(sid));
      newMentions = currentMentionsIds
        .filter((userId) => !currentNote.mentions.includes(userId))
        .reduce(
          (prev, userId) => {
            const userEmail = worksiteUsers.find((u) => u.id === userId)?.email;
            return userEmail
              ? {...prev, [userEmail]: currentMentions[userId]}  // key := email, value := fullname
              : prev
          },
          {}
        );
    } catch (e) {
      push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
      throwError(e);
      return;
    }

    setSubmittingCurrentNote(true);
    try {
      let noteId = currentNote.id;
      if (!noteId) {
        // --- CASE A: create new note
        const result = await countryApiRequest.post(`worksites_notes`, {
          cod_worksite: worksite.id,
          cod_worksite_activity: activityId,
          cod_author: userData.id,
          val_author: `${userData.firstname} ${userData.lastname}`,
          val_author_role_type: userData.role_type,
          val_contents: rawEditorContent,
          lst_mentions: currentMentionsIds,
          flg_deleted: false,
          edited_at: null,
          r_perm: ['public'],
          w_perm: ['public'],
          attachments_urls: {'data': currentNoteAttachments}
        });
        result['editorState'] = EditorState.createWithContent(convertFromRawWithCensorship(result.val_contents));

        setNotes((prev) => sortMode === 'asc'
          ? [...prev, result]
          : [result, ...prev]
        );

        // save note id to be used in email notification
        noteId = result.id;
      }
      else {
        // --- CASE B: update existing note
        const result = await countryApiRequest.put(`worksites_notes/${noteId}`, {
          ...notes.find((n) => n.id === noteId),
          val_contents: rawEditorContent,
          lst_mentions: currentMentionsIds,
          edited_at: new Date(),
          attachments_urls: {'data': currentNoteAttachments}
        });
        setNotes((prev) => prev.map((n) => n.id === noteId
          ? {...result, editorState: EditorState.createWithContent(convertFromRawWithCensorship(result.val_contents))}
          : n
        ));
      }
      push({type: 'success', title: intl.formatMessage({id: !!noteId ? 'note_correctly_updated' : 'note_correctly_inserted'})})
      setCurrentNote(null);

      // --- send email notification to the mentioned users (iff they weren't already mentioned before edit)
      if (Object.keys(newMentions).length > 0)
        await sendEmailNotificationToUsers(noteId, newMentions);
    }
    catch (err) {
      push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
      throwError(err);
      return;
    }
    finally {
      setSubmittingCurrentNote(false);
      setCurrentNoteAttachments([])
    }
  }

  // ---------- DELETE NOTE METHOD
  const deleteNote = async () => {
    if (!idToDelete || !notes.find((n) => n.id === idToDelete))
      return;
    setCommentsCount((prev) => prev - 1);

    try {
      const targetNote = notes.find((n) => n.id === idToDelete);
      await countryApiRequest.put(`worksites_notes/${idToDelete}`, {...targetNote, flg_deleted: true});
      push({type: 'success', title: intl.formatMessage({id: 'note_correctly_deleted'})});
      setNotes((prev) => prev.filter((n) => n.id !== idToDelete));
      setIdToDelete(null);  // close modal
      setCurrentNoteAttachments([])
    }
    catch (err) {
      push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
      throw err;
    }
  }

  // ---------- LOAD NOTES METHOD
  const fetchNotes = async (reset) => {
    if (loadingNotes)
      return;

    setLoadingNotes(true);
    try {

      const params = {
        cod_worksite: worksite.id,
        flg_deleted: false,
        skip: reset ? 0 : paginationSkip,
        limit: paginationBuffer,
        [sortMode]: 'created_at',
      };

      // request data
      const path = '/worksites_notes' + (!!activityId
        ? `/from_task_activity/${activityId}`
        : ''
      )
      const result = await countryApiRequest.get(path, {params});
      setCommentsCount((prev) => reset ? result.length : prev + result.length);

      // add editor state to each loaded note (needed in order to render mentions)
      result.forEach((note, idx, arr) => {
        arr[idx] = {...note, editorState: EditorState.createWithContent(convertFromRawWithCensorship(note.val_contents))};
      })

      setAllNotesLoaded(result.length < paginationBuffer);
      setPaginationSkip((prev) => reset ? result.length : prev + result.length);
      setNotes((prev) => reset ? result : [...prev, ...result]);

      // scroll to bottom if there is a highlighted note and it's not present in the retrieved set
      if (highlightedNoteId && !highlightedNoteFound) {
        const highlightedNoteFoundInThisBatch = result.length > 0 && result.some((n) => n.id === highlightedNoteId);
        if (!highlightedNoteFoundInThisBatch)
          mainContainer.current?.scrollTo({top: mainContainer.current.scrollHeight});
        else
          setHighlightedNoteFound(true);
      }
    }
    catch (error) {
      push({type: "error", title: intl.formatMessage({id: "server_error"})});
      throw error;
    }
    finally {
      setLoadingNotes(false);
    }
  };

  // ---------- LOAD NOTES SCROLL HANDLER
  const handleScroll = (e) => {
    if (!infiniteScroll || allNotesLoaded)
      return;

    const bottomReached = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottomReached)
      fetchNotes(false);
  }

  const scrollToNote = useCallback((node) => {
    if (scrollToHighlightedNoteHappened || loadingNotes)
      return;
    node?.scrollIntoView({block: 'start'});
    setScrollToHighlightedNoteHappened(true);
  }, [loadingNotes])  // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- HOOKS
  useEffect(() => {
    setMentionsSuggestions(worksiteUsers);

    return () => {
      // when component is unmounted, mark notes notifications as read
      const getConditions = (n) => !!activityId
        ? [n.val_type === 'activity_note', n.cod_worksite_activity === activityId]
        : [n.val_type === 'worksite_note', n.cod_worksite === worksite.id];
      const notificationIdsToDelete = userNotifications
        ?.filter((n) => getConditions(n).every((c) => c))
        ?.map((n) => n.id);

      if (notificationIdsToDelete?.length > 0)
        setUserNotifications((prev) => prev.filter((n) => !notificationIdsToDelete.includes(n.id)));
    }
  }, [worksiteUsers]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchNotes(true)
  }, [sortMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  return <>
    {/* ==================== NOTE EDITOR (NEW + EXISTING) ==================== */}
    <div className="flex-shrink-0">
      {
        currentNote
          ? (
            <div className={NOTE_CLS}>
              <p className="font-semibold">
                {
                  intl.formatMessage({id: !!currentNote.id ? 'edit_note' : 'new_note'})
                }
              </p>
              <div className="mt-2 mb-8 flex-shrink-0 my-auto">
                <div className="flex items-center gap-2 border-b pb-1 mb-1">
                  <div className="text-xs text-gray-400">
                    {intl.formatMessage({id: 'formatting'})}:
                  </div>
                  {
                    Object.entries(editorModifiers).map(([key, active]) => (
                      <div
                        key={key}
                        className={`cursor-pointer w-3 text-center ${active ? 'text-am-500 hover:text-gray-600' : 'text-gray-300 hover:text-gray-400'}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (key !== 'mention')
                            setEditorModifiers((prev) => ({...prev, [key]: !prev[key]}))
                          setCurrentNote((prev) => ({
                            ...prev,
                            content: EDITOR_CONTROLS[key].onClick(prev.content)}
                          ));
                        }}
                      >
                        {EDITOR_CONTROLS[key].component}
                      </div>
                    ))
                  }
                </div>
                <Editor
                  ref={editor}
                  editorKey={'editor'}
                  editorState={currentNote.content}
                  plugins={plugins}
                  onChange={(state) => { setCurrentNote((prev) => ({...prev, content: state})); }}
                  placeholder={intl.formatMessage({id: 'insert_text_here'})}
                />
                <MentionSuggestions
                  open={userSuggestionsOpen}
                  onOpenChange={(open) => { setUserSuggestionsOpen(open); }}
                  suggestions={mentionsSuggestions}
                  onSearchChange={({value}) => { setMentionsSuggestions(defaultSuggestionsFilter(value, worksiteUsers)); }}
                />
              </div>
              <NotesAttachmentsSection
                  worksiteId= {worksite.id}
                  noteAttachments={currentNoteAttachments}
                  setNoteAttachments={setCurrentNoteAttachments}
              />
              <div className="flex gap-2 items-center justify-end mb-1">
                <Button
                  type="button"
                  styleType="empty"
                  paddingCls="px-5 py-1"
                  onClick={() => { setCurrentNote(null); }}
                >
                  <span className="text-xs">{intl.formatMessage({id: 'cancel'})}</span>
                </Button>
                <Button
                  type="button"
                  paddingCls="px-5 py-1"
                  onClick={async () => { await saveCurrentNote(); }}
                  disabled={!currentNote || isCurrentNoteEmpty}
                  submitting={submittingCurrentNote}
                >
                  <span className="text-xs">{intl.formatMessage({id: 'save'})}</span>
                </Button>
              </div>
            </div>
          )
          : !viewOnly && <Button
            full
            onClick={() => { setCurrentNote(DEFAULT_NOTE); }}
          >
            <PlusCircleIcon className="w-5" />
            <span>{intl.formatMessage({id: 'add_note'})}</span>
          </Button>
      }
    </div>
    {notes?.length > 0 && <>
      <hr className="mt-4 mb-3" />
      <div
        onScroll={handleScroll}
        ref={mainContainer}
        className={`h-full overflow-y-auto ${loadingNotes ? 'pointer-events-none' : ''} ${infiniteScroll ? 'pb-24' : ''}`}
      >
        <Select
          value={sortMode}
          options={SORT_OPTIONS_NOTES.map((o) => ({...o, name: intl.formatMessage({id: o.name})}))}
          onChange={(evt) => { setSortMode(evt.target.value); }}
        />
        {/* ==================== EXISTING NOTES LIST ==================== */}
        <div className="flex flex-col gap-3">
          {
            notes.sort(notesSorter).map((n) => (
              <div
                key={n.id}
                ref={(ref) => {
                  if (n.id !== highlightedNoteId)
                    return;
                  scrollToNote(ref);
                }}
                className={`${NOTE_CLS.replace('px-3 py-2', '')} ${currentNote?.id === n.id ? 'pointer-events-none opacity-20' : null}`}
                onClick={() => { setHighlightedNoteClicked(true); }}
              >
                <div className={`px-3 py-2 ${highlightedNoteId === n.id && !highlightedNoteClicked ? 'border-l-8 border-kkgreen-400 bg-kkgreen-400 animate-pulse-bg-once' : ''}`}>
                  <div className="flex justify-between">
                    <div className="font-thin">
                      <span className="font-extrabold">{n.val_author}</span>
                      <span className="mx-1.5">&#8226;</span>
                      {intl.formatMessage({id: n.val_author_role_type})}
                    </div>
                    <div className="flex items-start gap-2 cursor-pointer">
                      {
                        userData.id === n.cod_author && (
                          <PencilSquareIcon
                            className="w-4 text-gray-400 hover:text-am-500"
                            onClick={() => {
                              setCurrentNote({
                                id: n.id,
                                content: n.editorState,
                                mentions: n.lst_mentions,
                              });
                              setCurrentNoteAttachments(n.attachments_urls.data)
                              mainContainer.current.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                          />
                        )
                      }
                      {
                        (CAN_DELETE_OTHERS_COMMENTS.includes(userData.role_type) || userData.id === n.cod_author) && (
                          <TrashIcon
                             className="w-4 text-gray-400 hover:text-red-500"
                             onClick={() => { setIdToDelete(n.id); }}
                          />
                        )
                      }
                    </div>
                  </div>
                  <p className="flex items-center gap-1">
                    {
                      userNotifications?.some((un) => un.val_type === 'worksite_note' && un.cod_note === n.id) && (
                        <NotificationPing />
                      )
                    }
                    <span className="text-xs">
                      {
                        intl.formatMessage(
                          {id: 'sent_on_timestamp'},
                          {
                            date: moment.utc(n.created_at).local().format('DD/MM/YYYY'),
                            time: moment.utc(n.created_at).local().format('HH:mm:ss')
                          }
                        )
                      }
                    </span>
                  </p>
                  <div className="text-sm my-2.5">
                    <Editor
                      editorState={n.editorState}
                      plugins={plugins}
                      onChange={(state) => {
                        // IMPORTANT: keep this even if it won't be used (it's needed to render mentions)
                        setNotes((prev) => prev.map((note) => note.id === n.id
                          ? {...note, editorState: state}
                          : note
                        ));
                      }}
                      readOnly  // this property renders the state as html
                    />
                  </div>
                  <NotesAttachmentsSection
                      worksiteId={worksite.id}
                      noteAttachments={n.attachments_urls ? n.attachments_urls.data : []}
                      disabled
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {
                        n.edited_at && intl.formatMessage(
                          {id: 'edited_on_timestamp'},
                          {
                            date: moment(n.edited_at).local().format('DD/MM/YYYY'),
                            time: moment(n.edited_at).local().format('HH:mm:ss')
                          }
                        )
                      }
                    </span>
                    {!viewOnly && (
                      <Button
                        paddingCls="px-3 py-0.5 my-0.5"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentNote({
                            content: getEditorWithMention(n.cod_author, n.val_author),
                            mentions: [],
                          });
                          mainContainer.current.scrollTo({top: 0, behavior: 'smooth'});
                        }}
                      >
                        <ArrowUturnRightIcon className="w-3" />
                        <span className="text-xs">{intl.formatMessage({id: 'reply'})}</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          }
        </div>
        {
          loadingNotes && infiniteScroll && (
            <div className="relative h-16">
              <Spinner styleType="white" />
            </div>
          )
        }
        {
          !infiniteScroll && !allNotesLoaded && <div className="mt-3 text-center">
            <Button
              type="button"
              textCls="text-xs"
              paddingCls="px-4 py-1.5"
              onClick={async () => { await fetchNotes(false); }}
              submitting={loadingNotes}
            >
              <ArrowPathIcon className="w-4" />
              <FormattedMessage id="load_more" />
            </Button>
          </div>
        }
      </div>
      <div className="absolute bg-gradient-to-b from-transparent to-white w-full h-10 bottom-0 pb-24" />
    </>}
    {/* ==================== NOTE DELETION MODAL ==================== */}
    {
      idToDelete && (
        <Modal
          type="warning"
          title={intl.formatMessage({id: "note_removal"})}
          description={intl.formatMessage({id: "note_removal_helper"})}
          onExit={() => { setIdToDelete(null); }}
          onDismiss={() => { setIdToDelete(null); }}
          onConfirm={async () => { await deleteNote(); }}
          confirmText={intl.formatMessage({id: 'confirm'})}
          dismissText={intl.formatMessage({id: 'cancel'})}
        />
      )
    }
  </>
}

export default WorksiteNotesSection;